import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useScheduleFormat } from '../hooks/use-schedule-format';
import { usePreviewSchedule, useScheduleLifecycle } from '../hooks/use-schedule-mutations';
import { canActivate, firstRunAtOf } from '../schemas/schedule-rules';

import { PreviewItems } from './preview-items';
import { RunCounts } from './run-counts';
import { ScheduleErrorAlert } from './schedule-error-alert';

export interface PreviewDialogProps {
  schedule: ProductSchedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Offer Activate in the footer (a draft, with `products.write`). */
  canWrite: boolean;
}

/**
 * The next batch as a dry run: the rule refresh, the release step and the selection per store,
 * computed by the same code a run uses and rolled back. A draft can be activated from here.
 */
export function PreviewDialog({ schedule, open, onOpenChange, canWrite }: PreviewDialogProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const preview = usePreviewSchedule(schedule.id);
  const activate = useScheduleLifecycle(schedule.id, 'activate');
  const format = useScheduleFormat(schedule.timeZone);
  const { mutate, reset } = preview;

  useEffect(() => {
    if (open) mutate();
    else reset();
  }, [mutate, open, reset]);

  const data = preview.data;
  const showActivate = canWrite && canActivate(schedule);
  // A draft's would-be first run, from the preview (or the schedule while it loads).
  const firstRunAt = firstRunAtOf(data) ?? firstRunAtOf(schedule);
  /** Close, Cancel, Escape and a successful activate all go here, so no old error comes back. */
  const close = () => {
    activate.reset();
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-3xl"
        data-tour="schedules.detail.preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('schedules:preview.title')}</DialogTitle>
          <DialogDescription>{t('schedules:preview.description')}</DialogDescription>
        </DialogHeader>
        {preview.isError ? <ScheduleErrorAlert error={preview.error} action="preview" /> : null}
        {activate.isError ? <ScheduleErrorAlert error={activate.error} action="activate" /> : null}
        {preview.isPending ? (
          <div className="flex flex-col gap-2" aria-busy>
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : null}
        {data ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm" role="status">
              {data.scheduledFor ? (
                <span title={format.utc(data.scheduledFor)}>
                  {t('schedules:preview.at', {
                    when: format.dateTime(data.scheduledFor),
                    zone: schedule.timeZone,
                  })}
                </span>
              ) : firstRunAt ? (
                <span title={format.utc(firstRunAt)}>
                  {t('schedules:preview.firstRun', {
                    when: format.dateTime(firstRunAt),
                    zone: schedule.timeZone,
                  })}
                </span>
              ) : (
                t('schedules:preview.notScheduled', {
                  runAt: schedule.runAt,
                  zone: schedule.timeZone,
                  start: format.dateTime(schedule.startsAt),
                })
              )}
            </p>
            <p className="text-sm">
              <RunCounts counts={data.counts} />
            </p>
            {data.rules ? (
              <p className="text-muted-foreground text-xs">
                {t('schedules:preview.rules', {
                  added: data.rules.added,
                  removed: data.rules.removed,
                  owned: data.rules.skippedOwned,
                })}
              </p>
            ) : null}
            {data.rules?.truncated ? (
              <Alert role="status">
                <AlertDescription>{t('schedules:warnings.rulesTruncated')}</AlertDescription>
              </Alert>
            ) : null}
            {data.items.length > 0 ? (
              <PreviewItems items={data.items} stores={schedule.stores} />
            ) : (
              <p className="text-muted-foreground text-sm">{t('schedules:preview.empty')}</p>
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            {t('common:actions.close')}
          </Button>
          {showActivate ? (
            <Button
              type="button"
              disabled={activate.isPending || preview.isPending}
              onClick={() => activate.mutate(undefined, { onSuccess: close })}
              data-tour="schedules.detail.preview-activate"
            >
              {t('schedules:actions.activate')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
